import logger from '../logger'
import type {
  AnalyticsAdapter,
  AnalyticsUserProperties,
  BaseEventProperties,
} from './types'

export class AnalyticsManager {
  private adapters: AnalyticsAdapter[] = []
  private initialized = false
  private defaultProperties: BaseEventProperties = {}

  register(adapter: AnalyticsAdapter): void {
    this.adapters.push(adapter)
  }

  // `apiKey` is optional because not every provider needs one — Datadog RUM is
  // booted at module load in each app's `instrumentation-client.ts`, so its
  // adapter's `init` is a no-op. Gating on a key here would silently disable
  // tracking for keyless providers.
  init(apiKey?: string): void {
    if (this.initialized) return

    this.adapters.forEach((adapter) => {
      try {
        adapter.init(apiKey)
      } catch (error) {
        logger.error(error, { feature: logger.Feature.Analytics })
      }
    })

    this.initialized = true
  }

  setDefaultProperties(properties: BaseEventProperties): void {
    this.defaultProperties = { ...this.defaultProperties, ...properties }

    this.adapters.forEach((adapter) => {
      try {
        adapter.setDefaultProperties?.(properties)
      } catch (error) {
        logger.error(error, { feature: logger.Feature.Analytics })
      }
    })
  }

  track(eventName: string, properties?: BaseEventProperties): void {
    if (!this.initialized) return

    const mergedProps = { ...this.defaultProperties, ...properties }

    this.adapters.forEach((adapter) => {
      try {
        adapter.track(eventName, mergedProps)
      } catch (error) {
        logger.error(error, { feature: logger.Feature.Analytics })
      }
    })
  }

  identify(userId: string): void {
    if (!this.initialized) return

    this.adapters.forEach((adapter) => {
      try {
        adapter.identify(userId)
      } catch (error) {
        logger.error(error, { feature: logger.Feature.Analytics })
      }
    })
  }

  setUserProperties(properties: AnalyticsUserProperties): void {
    if (!this.initialized) return

    this.adapters.forEach((adapter) => {
      try {
        adapter.setUserProperties(properties)
      } catch (error) {
        logger.error(error, { feature: logger.Feature.Analytics })
      }
    })
  }

  reset(): void {
    if (!this.initialized) return

    this.defaultProperties = {}

    this.adapters.forEach((adapter) => {
      try {
        adapter.reset()
      } catch (error) {
        logger.error(error, { feature: logger.Feature.Analytics })
      }
    })
  }

  async flush(): Promise<void> {
    if (!this.initialized) return

    await Promise.all(
      this.adapters.map(async (adapter) => {
        try {
          await adapter.flush()
        } catch (error) {
          logger.error(error, { feature: logger.Feature.Analytics })
        }
      }),
    )
  }
}
